import { useCallback, useEffect, useState } from 'react';
import { library } from '../store/useLibrary';
import type { Settings } from '../domain/types';

export type ThemePref = Settings['theme'];
export type ResolvedTheme = 'light' | 'dark' | 'hc';

export function resolveTheme(pref: ThemePref): ResolvedTheme {
  if (pref === 'auto') {
    if (window.matchMedia('(prefers-contrast: more)').matches) return 'hc';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return pref;
}

export function applyTheme(pref: ThemePref): ResolvedTheme {
  const r = resolveTheme(pref);
  document.documentElement.dataset.theme = r;
  document.documentElement.style.colorScheme = r === 'light' ? 'light' : 'dark';
  return r;
}

/** Read/write the persisted theme preference and keep the DOM in sync. */
export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(() => library.getSettings().theme);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => applyTheme(pref));

  const set = useCallback((next: ThemePref) => {
    setPref(next);
    setResolved(applyTheme(next));
    void library.setSetting('theme', next);
  }, []);

  useEffect(() => {
    if (pref !== 'auto') return;
    const queries = ['(prefers-color-scheme: dark)', '(prefers-contrast: more)'].map((q) =>
      window.matchMedia(q)
    );
    const onChange = () => setResolved(applyTheme('auto'));
    queries.forEach((m) => m.addEventListener('change', onChange));
    return () => queries.forEach((m) => m.removeEventListener('change', onChange));
  }, [pref]);

  return { pref, resolved, set };
}
